import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type {
  CommissionRateRow,
  CreateCommissionRateChangeInput,
  CreateCommissionRateInput,
  LineRow,
} from "../../api/commission-contracts";

const RATES = ["commission", "rates"];

/** GET /v1/commission/rates — current + history (newest effectiveFrom first),
 *  filterable by carrierId / lineCode. commission:read @ all. */
export function useCommissionRates(filters: { carrierId?: string; lineCode?: string } = {}) {
  const api = useApi();
  const params = new URLSearchParams();
  if (filters.carrierId) params.set("carrierId", filters.carrierId);
  if (filters.lineCode) params.set("lineCode", filters.lineCode);
  const qs = params.toString();
  return useQuery({
    queryKey: [...RATES, filters.carrierId ?? null, filters.lineCode ?? null],
    queryFn: () =>
      api.get<{ data: CommissionRateRow[] }>(`/v1/commission/rates${qs ? `?${qs}` : ""}`),
  });
}

/** GET /v1/catalog/lines — the tri-pillar taxonomy (the line picker). catalog:read @ all. */
export function useLines() {
  const api = useApi();
  return useQuery({
    queryKey: ["catalog", "lines"],
    queryFn: () => api.get<Page<LineRow>>("/v1/catalog/lines"),
  });
}

/** POST /v1/commission/rates — genesis write of a carrier·line(·product) rate
 *  (commission:write @ all, audited). Append-only; an already-open rate returns 409
 *  (use the change path to supersede it). On success, refetch the rate list. */
export function useCreateRate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommissionRateInput) =>
      api.post<CommissionRateRow>("/v1/commission/rates", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RATES });
    },
  });
}

/** POST /v1/commission/rates/change — the effective-dated change (commission:write @
 *  all, audited commission.rate-change): close the open rate, insert a new dated one.
 *  Append-only + reconstruction-preserved. On success, refetch so the prior rate shows
 *  closed and the new one open. Returns the new open CommissionRateRow. */
export function useChangeRate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommissionRateChangeInput) =>
      api.post<CommissionRateRow>("/v1/commission/rates/change", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RATES });
    },
  });
}
